import { Component, OnInit, OnDestroy, inject, signal, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ProjectService } from '../../services/project.service';
import { RecentProjectsService } from '../../services/recent-projects.service';
import { Subscription } from 'rxjs';

interface SidebarProject { id: string; name: string; }

/** Sidebar section: pinned projects + recently-opened projects, with pin toggles. */
@Component({
  selector: 'app-sidebar-projects',
  imports: [CommonModule],
  templateUrl: './sidebar-projects.html',
})
export class SidebarProjectsComponent implements OnInit, OnDestroy {
  private projectService = inject(ProjectService);
  recent = inject(RecentProjectsService);
  private router = inject(Router);

  private projects = signal<SidebarProject[]>([]);
  private sub?: Subscription;

  private static readonly PALETTE = ['#3b82f6', '#8b5cf6', '#22c55e', '#f59e0b', '#ec4899', '#06b6d4', '#ef4444', '#0ea5e9'];

  ngOnInit() {
    this.sub = this.projectService.projects$.subscribe(ps => {
      this.projects.set(ps.filter(p => p.projectId).map(p => ({ id: p.projectId!, name: p.projectName })));
    });
  }

  ngOnDestroy() {
    this.sub?.unsubscribe();
  }

  private byId = computed(() => {
    const m = new Map<string, SidebarProject>();
    for (const p of this.projects()) m.set(p.id, p);
    return m;
  });

  pinnedList = computed(
    () => this.recent.pinned().map(id => this.byId().get(id)).filter(Boolean) as SidebarProject[],
  );

  recentList = computed(() => {
    const pinnedSet = new Set(this.recent.pinned());
    return this.recent
      .recent()
      .filter(id => !pinnedSet.has(id))
      .map(id => this.byId().get(id))
      .filter(Boolean)
      .slice(0, 4) as SidebarProject[];
  });

  color(id: string): string {
    let h = 0;
    for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
    return SidebarProjectsComponent.PALETTE[h % SidebarProjectsComponent.PALETTE.length];
  }

  open(id: string) {
    this.router.navigate(['/projects', id]);
  }

  togglePin(event: Event, id: string) {
    event.stopPropagation();
    this.recent.togglePin(id);
  }
}
